import type {
  ContainsGroup,
  Exercise,
  FlareEpisode,
  GutScore,
  InsightBrief,
  ItemStat,
  Meal,
  Symptom,
  WeeklyInsight,
} from './types';
import {
  computeLongitudinalIntoleranceStats,
  computeMotilityKinetics,
  inferConstipationPeriods,
  inferTransitSpeedFromSymptoms,
} from './correlationEngine';
import { getMealGroups, getMealIngredients, normaliseIngredientName } from './ingredientTaxonomy';
import { buildFlareContext, computeItemStat } from './insightStats';
import { computeLeadHints, computePatternCandidates, computeSuspectOverlaps } from './insightOverlaps';
import { computeGutScore } from './gutScore';
import { encodeTimeline } from './insightTimeline';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const GROUP_ORDER: ContainsGroup[] = ['gluten', 'soy', 'soy (trace)', 'dairy'];
const CONF_WEIGHT = { low: 1, medium: 2, high: 3 } as const;
const pad = (n: number) => String(n).padStart(2, '0');

function localDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Device-local "YYYY-MM-DD HH:mm" (the server cannot localise). */
function localStamp(ms: number): string {
  return `${localDate(ms)} ${pad(new Date(ms).getHours())}:${pad(new Date(ms).getMinutes())}`;
}

function flareSeverity(s: Symptom): number {
  const vals = [s.bloating_score || 0, s.soreness_score || 0, ...Object.values(s.symptom_scores || {})];
  let max = Math.max(0, ...vals);
  if (typeof s.stool_type === 'number' && (s.stool_type <= 2 || s.stool_type >= 6)) max = Math.max(max, 6);
  return max;
}

function flareSymptomNames(s: Symptom): string[] {
  const ids = new Set<string>();
  for (const [id, v] of Object.entries(s.symptom_scores || {})) if (v >= 5) ids.add(id);
  if (s.bloating_score >= 5) ids.add('bloating');
  if (s.soreness_score >= 5) ids.add('stomach_pain');
  if (typeof s.stool_type === 'number' && (s.stool_type <= 2 || s.stool_type >= 6)) ids.add('stool_type');
  return [...ids].sort();
}

function mealLabel(meal: Meal): string {
  const titles = (meal.dishes ?? []).map((d) => (d.title ?? '').trim()).filter(Boolean);
  return titles.length ? titles.join(' + ') : meal.meal_type;
}

export {
  ALONE_MARGIN,
  MIN_CANDIDATE_MEALS,
  NEAR_MARGIN,
  PATTERN_CONDITIONS,
  PATTERN_MARGIN,
  PATTERN_OVER_ITEM,
  MIN_FLARES,
  MIN_BYSTANDER_APART,
  SOLID_MIN_MEALS,
  WILSON_Z,
  clearlyAbove,
  wilsonLower,
  computeLeadHints,
  computePatternCandidates,
  computeSuspectOverlaps,
  labelOverlap,
} from './insightOverlaps';

/** Members kept per group in a stored brief (the prompt shows the same top 10). */
export const STORED_MEMBER_CAP = 10;

/**
 * The brief as stored with the insight: no timeline or older summary, and each group's members capped
 * to the 10 most eaten (memberTotal keeps the full count), so stored insights stay small.
 */
export function stripBriefForStorage(b: InsightBrief): InsightBrief {
  const componentStats = (b.componentStats ?? []).map((c) => {
    const members = c.memberStats;
    if (!members || members.length <= STORED_MEMBER_CAP) return c;
    const top = [...members]
      .sort((x, y) => y.timesEaten - x.timesEaten || x.name.localeCompare(y.name))
      .slice(0, STORED_MEMBER_CAP);
    return { ...c, memberStats: top, memberTotal: members.length };
  });
  return { ...b, timeline: '', olderSummary: '', componentStats };
}

export function buildInsightBrief(
  meals: Meal[],
  symptoms: Symptom[],
  exercises: Exercise[],
  opts: { nowMs: number; previous?: WeeklyInsight }
): InsightBrief {
  const { nowMs, previous } = opts;
  const ctx = buildFlareContext(meals, symptoms);

  // Group meals by raw ingredient and by contains group.
  const byIngredient = new Map<string, Meal[]>();
  const memberGroups = new Map<string, Set<ContainsGroup>>();
  const byGroup = new Map<ContainsGroup, Meal[]>();
  for (const meal of meals) {
    const seen = new Set<string>();
    for (const ing of getMealIngredients(meal)) {
      if (!memberGroups.has(ing.name)) memberGroups.set(ing.name, new Set());
      ing.contains.forEach((g) => memberGroups.get(ing.name)!.add(g));
      if (seen.has(ing.name)) continue;
      seen.add(ing.name);
      if (!byIngredient.has(ing.name)) byIngredient.set(ing.name, []);
      byIngredient.get(ing.name)!.push(meal);
    }
    for (const g of new Set(getMealGroups(meal))) {
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g)!.push(meal);
    }
  }

  const allStats = [...byIngredient.entries()]
    .map(([name, ms]) => computeItemStat(name, ms, ctx))
    .sort((a, b) => b.timesEaten - a.timesEaten || a.name.localeCompare(b.name));
  const statByName = new Map(allStats.map((s) => [s.name, s]));

  const componentStats: ItemStat[] = GROUP_ORDER.map((g) => {
    const stat = computeItemStat(g, byGroup.get(g) ?? [], ctx);
    stat.memberStats = allStats.filter((s) => memberGroups.get(s.name)?.has(g) && s.timesEaten >= 1);
    return stat;
  });
  const groupLift = new Map(componentStats.map((c) => [c.name, c.lift]));

  const rank = (s: ItemStat) => s.lift * CONF_WEIGHT[s.confidence];
  const ingredientStats = allStats
    .filter((s) => s.timesEaten >= 2 && s.lift > 1.0)
    .sort((a, b) => rank(b) - rank(a) || b.timesEaten - a.timesEaten || a.name.localeCompare(b.name))
    .slice(0, 8);
  const safeIngredients = allStats.filter((s) => s.lift <= 1.0).slice(0, 8);

  const suspectOverlaps = computeSuspectOverlaps(
    meals,
    ctx,
    ingredientStats.map((s) => s.name),
    componentStats.filter((c) => c.timesEaten > 0).map((c) => c.name as ContainsGroup),
    memberGroups
  );

  const patternCandidates = computePatternCandidates(
    meals,
    symptoms,
    ctx,
    [...ingredientStats, ...componentStats.filter((c) => c.timesEaten > 0)],
    suspectOverlaps
  );

  const leadHints = computeLeadHints(
    [...componentStats.filter((c) => c.timesEaten > 0), ...ingredientStats],
    suspectOverlaps,
    ctx.baselineRate
  );

  const speed = inferTransitSpeedFromSymptoms(symptoms);
  const suspectNames = new Set(ingredientStats.map((s) => s.name));
  const ruledOut = computeLongitudinalIntoleranceStats(meals, symptoms, undefined, speed)
    .filter((i) => i.isExoneratedByCrossReference)
    .map((i) => ({
      name: normaliseIngredientName(i.tag),
      reason: i.crossReferenceNote || 'Ruled out by calm-meal cross-referencing',
    }))
    .filter((r) => !suspectNames.has(r.name))
    .slice(0, 8);

  const kinetics = computeMotilityKinetics(symptoms, meals, speed);
  const motility = {
    perDay: kinetics.evacuationRhythmPerDay,
    longestGapHours: Math.max(0, ...inferConstipationPeriods(symptoms, meals, speed).map((p) => p.unloggedHours)),
    bloatGapDelta: kinetics.bloatDeltaOnGapDays,
  };

  const recentFlares = ctx.flares.filter((f) => f.atMs <= nowMs && f.atMs > nowMs - 14 * DAY_MS);
  const worstFlares: FlareEpisode[] = recentFlares
    .map((f) => ({ f, severity: flareSeverity(f.symptom) }))
    .sort((a, b) => b.severity - a.severity || b.f.atMs - a.f.atMs)
    .slice(0, 5)
    .map(({ f, severity }) => {
      const candidates = meals
        .map((m) => ({ m, ms: new Date(m.timestamp_iso).getTime() }))
        .filter(({ ms }) => Number.isFinite(ms) && ms < f.atMs && ms >= f.atMs - ctx.windowHours * HOUR_MS)
        .map(({ m, ms }) => {
          const ings = getMealIngredients(m);
          const lifts = [
            ...ings.map((i) => statByName.get(i.name)?.lift ?? 0),
            ...getMealGroups(m).map((g) => groupLift.get(g) ?? 0),
          ];
          return { m, ms, lift: Math.max(0, ...lifts), ings };
        })
        .sort((a, b) => b.lift - a.lift || b.ms - a.ms)
        .slice(0, 3);
      return {
        at: localStamp(f.atMs),
        severity,
        symptoms: flareSymptomNames(f.symptom),
        candidateMeals: candidates.map(({ m, ms, ings }) => ({
          at: localStamp(ms),
          label: mealLabel(m),
          ingredients: ings.map((i) => i.name),
          groups: [...new Set(getMealGroups(m))],
          lagHours: Math.round((f.atMs - ms) / HOUR_MS),
        })),
      };
    });

  const gutScore: GutScore | null = computeGutScore(meals, symptoms, nowMs);
  const previousGutScore = computeGutScore(meals, symptoms, nowMs - 7 * DAY_MS)?.score ?? null;

  const eventMs = [...meals, ...symptoms, ...exercises]
    .map((e) => new Date(e.timestamp_iso).getTime())
    .filter((n) => Number.isFinite(n));
  const days = new Set<string>();
  for (const e of [...meals, ...symptoms]) {
    const ms = new Date(e.timestamp_iso).getTime();
    if (Number.isFinite(ms)) days.add(localDate(ms));
  }
  const window = {
    from: localDate(eventMs.length ? Math.min(...eventMs) : nowMs),
    to: localDate(nowMs),
    daysLogged: days.size,
  };

  let changesSinceLast: InsightBrief['changesSinceLast'] = null;
  if (previous) {
    const prev = previous.brief;
    const diff = (a: string[], b: string[]) => a.filter((n) => !b.includes(n));
    const curSuspects = ingredientStats.map((s) => s.name);
    const prevSuspects = (prev?.ingredientStats ?? []).map((s) => s.name);
    const curRuled = ruledOut.map((r) => r.name);
    const prevRuled = (prev?.ruledOut ?? []).map((r) => r.name);
    const planTitles = previous.ai?.plan?.length
      ? previous.ai.plan.map((p) => p.title)
      : (previous.action_plans ?? []).map((p) => p.title);
    changesSinceLast = {
      newSuspects: prev ? diff(curSuspects, prevSuspects) : [],
      droppedSuspects: prev ? diff(prevSuspects, curSuspects) : [],
      newlyRuledOut: prev ? diff(curRuled, prevRuled) : [],
      // A legacy insight's health_score was AI-made, so only a v2 score is compared directly; otherwise
      // the delta is against the gut score computed for the week before.
      scoreDelta: !gutScore
        ? null
        : previous.format_version === 2 && typeof previous.health_score === 'number'
        ? gutScore.score - previous.health_score
        : previousGutScore !== null
        ? gutScore.score - previousGutScore
        : null,
      previousPlanTitles: planTitles,
    };
  }

  const { timeline, olderSummary } = encodeTimeline(meals, symptoms, exercises, { nowMs });

  return {
    version: 1,
    generatedAtIso: new Date(nowMs).toISOString(),
    window,
    gutScore,
    previousGutScore,
    componentStats,
    ingredientStats,
    safeIngredients,
    ruledOut,
    motility,
    worstFlares,
    changesSinceLast,
    timeline,
    olderSummary,
    baselineFlareRate: Math.round(ctx.baselineRate * 100) / 100,
    suspectOverlaps,
    patternCandidates,
    leadHints,
  };
}
